/**
 * Hub-native work order BOM deviation modal. Updates the Supabase BOM in place
 * (no Megaventory rebuild). Megaventory is only touched at receive.
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { useQuery } from "@tanstack/react-query";
import { AlertTriangle, HelpCircle, Plus, X } from "lucide-react";

import { AssemblySkuCombobox } from "@/components/mrp/AssemblySkuCombobox";
import { MrpSkuCatalogHoverHintBody } from "@/components/mrp/MrpSkuCatalogHoverHintBody";
import { MrpSkuWithUnitLabel } from "@/components/mrp/MrpSkuWithUnitLabel";
import { Button } from "@/components/ui/Button";
import { Card, CardBody, CardHeader } from "@/components/ui/Card";
import { hubModalOverlayProps } from "@/components/ui/HubModalOverlay";
import { Input, Label } from "@/components/ui/Input";
import { CardTitleWithHelp, HoverHintTrigger, HoverHintWithGlyph, ReadOnlyHoverLayer } from "@/components/ui/title-help";
import { ApiError, apiFetch } from "@/lib/api";
import type { MrpSkuHoverHintFields } from "@/lib/mrp";
import { FLOATING_POPOVER_Z_INDEX } from "@/lib/viewportAnchoredPopover";

import {
  WORK_ORDER_DEVIATION_HELP,
  buildDeviationApplyPayload,
  deviationNeedsAllocationResolution,
  deviationRowsFromBom,
  formatDeviationChangeHoverHint,
  formatDeviationStatus,
  isOpenHubWorkOrderStatus,
  remainingFinishedGoodQuantity,
  type WorkOrderDeviationApplyPayload,
  type WorkOrderDeviationRecordPayload,
  type WorkOrderDeviationRow,
} from "./workOrderDeviationUtils";
import {
  effectiveWorkOrderOrderedQuantity,
  effectiveWorkOrderReceivedQuantity,
  formatCount,
  type WorkOrderDetail,
} from "./workOrdersPageModel";

export type WorkOrderDeviationRecord = {
  id: number | string;
  created_at: string;
  created_by: string | null;
  reason: string;
  status: string;
  payload: WorkOrderDeviationRecordPayload;
};

function deviationQuantityChanged(row: WorkOrderDeviationRow): boolean {
  if (row.isNew) return false;
  return (
    row.originalQuantityPerFinishedGood.trim() !== "" &&
    row.quantityPerFinishedGood.trim() !== row.originalQuantityPerFinishedGood.trim()
  );
}

function WorkOrderDeviationBomLineCell({
  row,
  hint,
  hintsLoading,
  onHoverIntent,
  disabled,
  onSkuChange,
}: {
  row: WorkOrderDeviationRow;
  hint: MrpSkuHoverHintFields | undefined;
  hintsLoading: boolean;
  onHoverIntent: () => void;
  disabled: boolean;
  onSkuChange: (sku: string) => void;
}) {
  const sku = row.componentSku.trim();
  const substituted =
    row.originalComponentSku != null &&
    row.originalComponentSku.trim() !== "" &&
    sku.toLowerCase() !== row.originalComponentSku.trim().toLowerCase();

  return (
    <div className="flex min-w-[12rem] items-start gap-1">
      <div className="min-w-0 flex-1">
        <AssemblySkuCombobox
          value={row.componentSku}
          onChange={onSkuChange}
          placeholder="Component SKU"
          disabled={disabled}
        />
        {row.isNew ? (
          <div className="mt-1 font-mono text-[11px] text-slate-500">was none</div>
        ) : substituted ? (
          <div className="mt-1 font-mono text-[11px] text-slate-500">
            was {row.originalComponentSku}
          </div>
        ) : null}
      </div>
      {sku !== "" ? (
        <HoverHintTrigger
          align="start"
          portalZIndex={FLOATING_POPOVER_Z_INDEX}
          trigger={
            <button
              type="button"
              className="inline-flex h-8 shrink-0 items-center rounded p-0.5 text-slate-400 hover:text-slate-700"
              aria-label={`Catalog details for ${sku}`}
              onPointerEnter={onHoverIntent}
              onFocus={onHoverIntent}
            >
              <HelpCircle className="h-3.5 w-3.5" aria-hidden />
            </button>
          }
        >
          <MrpSkuCatalogHoverHintBody sku={sku} hint={hint} loading={hintsLoading} />
        </HoverHintTrigger>
      ) : null}
      {sku !== "" && hint?.mv_unit ? (
        <span className="sr-only">
          <MrpSkuWithUnitLabel sku={sku} unit={hint.mv_unit} />
        </span>
      ) : null}
    </div>
  );
}

export function WorkOrderDeviationDialog({
  workOrder,
  deviations,
  onClose,
  onConfirm,
  isSubmitting,
  error,
  successMessage,
  hubWritesAllowed = true,
}: {
  workOrder: WorkOrderDetail;
  deviations: WorkOrderDeviationRecord[];
  onClose: () => void;
  onConfirm: (payload: WorkOrderDeviationApplyPayload) => void;
  isSubmitting: boolean;
  error: ApiError | null;
  successMessage: string | null;
  hubWritesAllowed?: boolean;
}) {
  const priorOrderedQuantity = effectiveWorkOrderOrderedQuantity(workOrder);
  const receivedFinishedGoodQuantity = effectiveWorkOrderReceivedQuantity(workOrder);
  const initialRemainingFg = remainingFinishedGoodQuantity(
    priorOrderedQuantity,
    receivedFinishedGoodQuantity,
  );

  const [reason, setReason] = useState("");
  const [orderedQuantity, setOrderedQuantity] = useState(String(priorOrderedQuantity));
  const [rows, setRows] = useState<WorkOrderDeviationRow[]>(() =>
    deviationRowsFromBom(workOrder.bom, initialRemainingFg),
  );
  const [hintsFetchEnabled, setHintsFetchEnabled] = useState(false);

  useEffect(() => {
    const remainingFg = remainingFinishedGoodQuantity(
      effectiveWorkOrderOrderedQuantity(workOrder),
      effectiveWorkOrderReceivedQuantity(workOrder),
    );
    setReason("");
    setOrderedQuantity(String(effectiveWorkOrderOrderedQuantity(workOrder)));
    setRows(deviationRowsFromBom(workOrder.bom, remainingFg));
  }, [workOrder.id, workOrder.bom]);

  const parsedOrderedQuantity = useMemo(() => {
    const parsed = Number(orderedQuantity.trim());
    return Number.isFinite(parsed) ? Math.round(parsed) : priorOrderedQuantity;
  }, [orderedQuantity, priorOrderedQuantity]);

  const remainingFg = remainingFinishedGoodQuantity(
    parsedOrderedQuantity,
    receivedFinishedGoodQuantity,
  );

  const parsedPayload = useMemo(
    () =>
      buildDeviationApplyPayload({
        reason,
        originalBom: workOrder.bom,
        rows,
        priorOrderedQuantity,
        receivedFinishedGoodQuantity,
        newOrderedQuantityRaw: orderedQuantity,
      }),
    [
      reason,
      workOrder.bom,
      rows,
      priorOrderedQuantity,
      receivedFinishedGoodQuantity,
      orderedQuantity,
    ],
  );

  const pendingAllocationWarning = useMemo(() => {
    if ("error" in parsedPayload) return false;
    const newOrdered =
      parsedPayload.newOrderedQuantity != null
        ? parsedPayload.newOrderedQuantity
        : priorOrderedQuantity;
    return deviationNeedsAllocationResolution({
      originalBom: workOrder.bom,
      bomLineChanges: parsedPayload.bomLineChanges ?? [],
      priorOrderedQuantity,
      newOrderedQuantity: newOrdered,
      receivedFinishedGoodQuantity,
    });
  }, [parsedPayload, priorOrderedQuantity, receivedFinishedGoodQuantity, workOrder.bom]);

  const componentSkus = useMemo(
    () => [...new Set(rows.map((row) => row.componentSku.trim()).filter((sku) => sku.length > 0))],
    [rows],
  );

  const enableHintsFetch = useCallback(() => {
    setHintsFetchEnabled(true);
  }, []);

  const skuHoverHintsQuery = useQuery<{ sku_hover_hints: Record<string, MrpSkuHoverHintFields> }, ApiError>({
    queryKey: ["mrp", "sku-hover-hints", "work-order-deviation", componentSkus.join("\n")],
    queryFn: () =>
      apiFetch<{ sku_hover_hints: Record<string, MrpSkuHoverHintFields> }>("/api/mrp/sku-hover-hints", {
        query: { cpns: componentSkus.join(",") },
      }),
    enabled: hintsFetchEnabled && componentSkus.length > 0,
    staleTime: 60_000,
  });

  const skuHoverHints = skuHoverHintsQuery.data?.sku_hover_hints;
  const hintsLoading =
    hintsFetchEnabled && (skuHoverHintsQuery.isPending || skuHoverHintsQuery.isFetching);

  const blocked = !isOpenHubWorkOrderStatus(workOrder.hub_status);
  const inputsDisabled = !hubWritesAllowed || blocked;

  function setRow(rowKey: string, patch: Partial<WorkOrderDeviationRow>): void {
    setRows((current) => current.map((row) => (row.rowKey === rowKey ? { ...row, ...patch } : row)));
  }

  function addRow(): void {
    if (inputsDisabled) return;
    const id = `new-${Date.now()}-${rows.length}`;
    setRows((current) => [
      ...current,
      {
        rowKey: id,
        originalComponentSku: null,
        componentSku: "",
        quantityPerFinishedGood: "1",
        originalQuantityPerFinishedGood: "",
        quantityAllocated: 0,
        quantityConsumed: 0,
        isNew: true,
      },
    ]);
  }

  function handleConfirm(): void {
    if ("error" in parsedPayload) return;
    onConfirm(parsedPayload);
  }

  return createPortal(
    <div {...hubModalOverlayProps(false)} role="dialog" aria-modal="true">
      <Card className="my-4 w-full max-w-3xl shadow-xl">
        <CardHeader>
          <div className="flex items-start justify-between gap-3">
            <CardTitleWithHelp hoverHint={WORK_ORDER_DEVIATION_HELP}>
              BOM deviation — {workOrder.mv_number ?? workOrder.number}
            </CardTitleWithHelp>
            <button
              onClick={onClose}
              className="rounded-md border border-slate-200 bg-white p-1 text-slate-500 hover:text-slate-900"
              aria-label="Close BOM deviation"
              type="button"
            >
              <X className="h-4 w-4" aria-hidden />
            </button>
          </div>
        </CardHeader>
        <CardBody className="space-y-4">
          {blocked ? (
            <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">
              Work order status is <strong>{workOrder.hub_status ?? "unknown"}</strong>. Deviations are only
              allowed on open hub-native work orders.
            </div>
          ) : null}

          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <Label htmlFor="deviation-reason">Reason</Label>
              <Input
                id="deviation-reason"
                className="mt-1.5"
                value={reason}
                disabled={inputsDisabled}
                onChange={(event) => setReason(event.target.value)}
                placeholder="Why is the BOM changing?"
              />
            </div>
            <div>
              <Label htmlFor="deviation-ordered-qty">Ordered finished-good quantity</Label>
              <Input
                id="deviation-ordered-qty"
                className="mt-1.5 font-mono"
                type="number"
                min={Math.max(1, receivedFinishedGoodQuantity)}
                step="1"
                value={orderedQuantity}
                disabled={inputsDisabled}
                onChange={(event) => setOrderedQuantity(event.target.value)}
              />
              <p className="mt-1 text-xs text-slate-500">
                {formatCount(receivedFinishedGoodQuantity)} received · {formatCount(remainingFg)} remaining to
                build · BOM qty / FG applies to remaining units only
              </p>
            </div>
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label>BOM lines (quantity per finished good for remaining units)</Label>
              <ReadOnlyHoverLayer
                active={!hubWritesAllowed}
                hoverHint="Your role has read-only access to the Manufacturing hub module."
                layout="inline"
              >
                <Button variant="ghost" size="sm" onClick={addRow} disabled={inputsDisabled}>
                  <Plus className="h-4 w-4" aria-hidden /> Add line
                </Button>
              </ReadOnlyHoverLayer>
            </div>
            <div
              className="overflow-x-auto rounded-md border border-slate-200"
              onPointerEnter={enableHintsFetch}
            >
              <table className="min-w-full divide-y divide-slate-200 text-sm">
                <thead className="bg-slate-50 text-xs uppercase text-slate-600">
                  <tr>
                    <th className="px-2 py-1.5 text-left">Component</th>
                    <th className="px-2 py-1.5 text-right">Qty / FG</th>
                    <th className="px-2 py-1.5 text-right">Allocated</th>
                    <th className="px-2 py-1.5 text-right">Consumed</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {rows.map((row) => {
                    const parsedQty = Number(row.quantityPerFinishedGood.trim());
                    const zeroed =
                      row.quantityPerFinishedGood.trim() !== "" &&
                      Number.isFinite(parsedQty) &&
                      parsedQty === 0;
                    const qtyChanged = deviationQuantityChanged(row);
                    return (
                      <tr
                        key={row.rowKey}
                        className={zeroed ? "bg-slate-50 text-slate-500" : undefined}
                      >
                        <td className="px-2 py-1.5 align-top">
                          <WorkOrderDeviationBomLineCell
                            row={row}
                            hint={skuHoverHints?.[row.componentSku.trim()]}
                            hintsLoading={hintsLoading}
                            onHoverIntent={enableHintsFetch}
                            disabled={inputsDisabled}
                            onSkuChange={(next) => setRow(row.rowKey, { componentSku: next })}
                          />
                        </td>
                        <td className="px-2 py-1.5 align-top text-right tabular-nums">
                          <Input
                            className="h-8 text-right font-mono text-xs"
                            type="number"
                            min="0"
                            step="0.0001"
                            value={row.quantityPerFinishedGood}
                            disabled={inputsDisabled}
                            onChange={(event) => {
                              const raw = event.target.value;
                              if (raw.trim() === "") {
                                setRow(row.rowKey, { quantityPerFinishedGood: "" });
                                return;
                              }
                              const next = Number(raw);
                              if (Number.isFinite(next) && next === 0) {
                                setRow(row.rowKey, { quantityPerFinishedGood: "0" });
                                return;
                              }
                              setRow(row.rowKey, { quantityPerFinishedGood: raw });
                            }}
                            placeholder="0"
                          />
                          {qtyChanged ? (
                            <div className="mt-1 font-mono text-[11px] text-slate-500">
                              was {row.originalQuantityPerFinishedGood}
                            </div>
                          ) : null}
                          {zeroed ? (
                            <div className="mt-1 text-[11px] font-medium uppercase tracking-wide text-slate-500">
                              Removed from remaining build
                            </div>
                          ) : null}
                        </td>
                        <td className="px-2 py-1.5 align-top text-right font-mono text-xs text-slate-700">
                          <div className="flex h-8 items-center justify-end">
                            {formatCount(row.quantityAllocated)}
                          </div>
                        </td>
                        <td className="px-2 py-1.5 align-top text-right font-mono text-xs text-slate-700">
                          <div className="flex h-8 items-center justify-end">
                            {formatCount(row.quantityConsumed)}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                  {rows.length === 0 ? (
                    <tr>
                      <td colSpan={4} className="px-3 py-4 text-center text-xs text-slate-500">
                        No BOM lines.
                      </td>
                    </tr>
                  ) : null}
                </tbody>
              </table>
            </div>
          </div>

          {pendingAllocationWarning ? (
            <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
              This deviation will be recorded as <strong>pending allocation resolution</strong>. Return
              allocated material for affected components before allocate or receive.
            </div>
          ) : null}

          {"error" in parsedPayload && reason.trim() !== "" ? (
            <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">
              {parsedPayload.error}
            </div>
          ) : null}

          {error ? (
            <div className="flex items-start gap-2 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">
              <AlertTriangle className="h-4 w-4 shrink-0" aria-hidden /> {error.message}
            </div>
          ) : null}

          {successMessage ? (
            <div className="rounded-md border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-800">
              {successMessage}
            </div>
          ) : null}

          {deviations.length > 0 ? (
            <div className="space-y-2 rounded-md border border-slate-200 bg-slate-50 p-3">
              <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                Prior deviations
              </div>
              <ul className="space-y-2 text-sm">
                {deviations.map((deviation) => (
                  <li key={String(deviation.id)} className="rounded border border-slate-100 bg-white px-2 py-2">
                    <div className="flex flex-wrap items-center gap-2">
                      <div className="flex items-center gap-1">
                        <span className="font-medium text-slate-900">#{String(deviation.id)}</span>
                        <HoverHintWithGlyph
                          hoverHint={formatDeviationChangeHoverHint(deviation.payload)}
                          portalZIndex={FLOATING_POPOVER_Z_INDEX}
                        />
                      </div>
                      <span className="text-xs text-slate-500">{formatDeviationStatus(deviation.status)}</span>
                    </div>
                    <div className="mt-1 text-xs text-slate-600">{deviation.reason}</div>
                  </li>
                ))}
              </ul>
            </div>
          ) : null}

          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={onClose} disabled={isSubmitting}>
              Cancel
            </Button>
            <ReadOnlyHoverLayer
              active={!hubWritesAllowed}
              hoverHint="Your role has read-only access to the Manufacturing hub module."
              layout="inline"
            >
              <Button
                onClick={handleConfirm}
                isLoading={isSubmitting}
                disabled={
                  !hubWritesAllowed ||
                  blocked ||
                  "error" in parsedPayload ||
                  isSubmitting
                }
              >
                Apply deviation
              </Button>
            </ReadOnlyHoverLayer>
          </div>
        </CardBody>
      </Card>
    </div>,
    document.body,
  );
}
